import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useStudioTheme } from '../hooks/useStudioTheme';
import Header from './Header';
import Footer from './Footer';
import './MockInterviewPage.css';
import './MockInterviewPage.theme.css';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import ProductSidebar from './ProductSidebar';
import CreditsBadge from './CreditsBadge';


// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

// Target question count for a full mock interview. Rendered in the
// header subtitle and pagination chip strip from the moment the test
// starts — even though we lazy-load (Q1 lands fast, 2-15 trickle in
// via background top-up), surfacing "of 15" gives the candidate the
// right mental model and makes locked slots feel intentional rather
// than missing.
const MOCK_TOTAL_QUESTIONS = 15;

const MockInterviewPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user, loading, logout, credits } = useUser();
  // Shared studio theme — also mirrors the class onto <body> so the portaled
  // dialogs below (exit confirm, credits, job form) get themed too.
  const themeClass = useStudioTheme(!embedded);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
  
  const [showJobForm, setShowJobForm] = useState(false);
  // Close the job form modal on Escape (mirrors click-outside on the overlay).
  useEffect(() => {
    if (!showJobForm) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowJobForm(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showJobForm]);
  const [formData, setFormData] = useState({
    jobRole: '',
    experienceLevel: '',
    company: '',
    jobDescription: '',
    resumeText: ''
  });
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [uploadedJobDescFile, setUploadedJobDescFile] = useState(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'
  const [fileUploadError, setFileUploadError] = useState('');
  
  // Mock test specific state
  const [mockTestStarted, setMockTestStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Branded exit-confirm modal state — replaces window.confirm() when
  // the candidate clicks Header / sidebar / profile or hits browser
  // back/forward mid-test. exitTargetRef holds the element they tried
  // to click so we can replay it after confirm. bypassGuardRef lets the
  // replayed click through our own capture-phase handler.
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitTargetRef = useRef(null);
  const bypassGuardRef = useRef(false);
  // Highest question index the candidate has reached via Save & Next.
  // Used to lock pagination chips for questions they haven't unlocked
  // yet — prevents skipping ahead and forces a sequential walkthrough.
  // Previous still works (re-visiting earlier questions is fine).
  const [furthestReached, setFurthestReached] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(45 * 60);
  const [recordingError, setRecordingError] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (embedded) return; // parent landing handles un-auth UX
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate, embedded]);

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  // Lock body scroll + add a marker class while the embedded test is in
  // fullscreen takeover mode, so the landing page underneath can't
  // bleed through scroll/focus during the session.
  useEffect(() => {
    // Only lock scroll during the live test. Once evaluation exists the
    // results view needs to scroll, so release the lock even though
    // mockTestStarted is still true.
    if (!embedded || !mockTestStarted || evaluation) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('mock-interview-active');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('mock-interview-active');
    };
  }, [embedded, mockTestStarted, evaluation]);

  // Navigation guard — once the test is running, accidental clicks on
  // the Header logo, profile menu, sidebar, or any external link would
  // wipe the in-progress session (no saved-state recovery). Match the
  // InterviewPage's safety pattern and add three layers of protection:
  //   1. beforeunload — tab close / reload / hard URL change
  //   2. document-level click capture — clicks on <a> or buttons inside
  //      header/nav chrome show a confirm and abort if denied
  //   3. popstate (browser back/forward) — same confirm
  // Everything inside .mil-studio (the test panel) is allowed through
  // so Save & Next / Previous / End Test / Record buttons still work.
  useEffect(() => {
    if (!mockTestStarted) return;

    // Native browser confirm copy — used on tab close / reload only.
    // In-app clicks + back/forward now route to the branded modal below.
    const CONFIRM_MSG = 'Your mock interview is still in progress. Leaving will save your progress to your dashboard.';

    // 1) Tab close / reload / hard navigation
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = CONFIRM_MSG;
      return CONFIRM_MSG;
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    // 2) In-app clicks — open the branded modal instead of window.confirm.
    const onCaptureClick = (e) => {
      if (bypassGuardRef.current) return; // confirm flow is replaying
      const target = e.target;
      if (!target || !target.closest) return;
      if (target.closest('.mil-studio')) return;
      if (target.closest('[data-allow-during-mock="true"]')) return;
      const anchor = target.closest('a[href]');
      const navBtn = target.closest('header, .header, nav, [role="navigation"], .product-sidebar, .header-avatar-img, .user-profile-container, .credits-display');
      if (!anchor && !navBtn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      exitTargetRef.current = anchor || navBtn || target;
      setShowExitConfirm(true);
    };
    document.addEventListener('click', onCaptureClick, true);

    // 3) Browser back/forward — re-pin the URL and pop the modal.
    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
      exitTargetRef.current = null;
      setShowExitConfirm(true);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onCaptureClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [mockTestStarted]);

  useEffect(() => {
    if (mockTestStarted && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleEndTest(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [mockTestStarted, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  // File processing functions
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file. Please ensure the PDF contains readable text.');
    }
  };

  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw new Error('Failed to parse DOCX file. Please try pasting the text manually.');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessingFile(true);
    setFileUploadError('');

    try {
      if (type === 'resume') {
        setUploadedResumeFile(file);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(file);
      }

      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsText(file);
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        extractedText = await extractTextFromPDF(file);
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx')
      ) {
        extractedText = await extractTextFromDOCX(file);
      } else {
        throw new Error('Unsupported file format. Please upload .txt, .pdf, or .docx files.');
      }

      extractedText = extractedText.trim();

      if (!extractedText) {
        throw new Error('No text could be extracted from the file.');
      }

      if (type === 'resume') {
        setFormData(prevData => ({
          ...prevData,
          resumeText: extractedText
        }));
        setFileUploadError('✓ Resume uploaded successfully!');
      } else if (type === 'jobDesc') {
        setFormData(prevData => ({
          ...prevData,
          jobDescription: extractedText
        }));
        setFileUploadError('✓ Job description uploaded successfully!');
      }

      setTimeout(() => {
        setFileUploadError('');
      }, 3000);

    } catch (error) {
      console.error('Error processing file:', error);
      setFileUploadError(error.message || 'Failed to process file.');
      
      if (type === 'resume') {
        setUploadedResumeFile(null);
      } else if (type === 'jobDesc') {
        setUploadedJobDescFile(null);
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleStartMockTest = async () => {
    if (formData.jobRole && formData.experienceLevel && formData.jobDescription && formData.resumeText) {
      try {
        setIsGeneratingQuestions(true);
        
        // Deduct credit
        const response = await fetch(`${API_BASE_URL}/api/mock-interview/deduct-credit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const data = await response.json();
          if (response.status === 403) {
            setShowJobForm(false);
            setShowInsufficientCreditsModal(true);
            setIsGeneratingQuestions(false);
            return;
          }
          throw new Error(data.error || 'Failed to deduct credit');
        }

        // Generate ONLY the first question synchronously so we can flip
        // into the live test in ~1.5s instead of 8-12s. Remaining 14
        // questions are fetched in the background while the candidate
        // reads/answers Q1.
        const questionsResponse = await fetch(`${API_BASE_URL}/api/mock-interview/generate-questions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jobRole: formData.jobRole,
            experienceLevel: formData.experienceLevel,
            company: formData.company,
            jobDescription: formData.jobDescription,
            resumeText: formData.resumeText,
            count: 1,
          })
        });

        if (!questionsResponse.ok) {
          throw new Error('Failed to generate questions');
        }

        const questionsData = await questionsResponse.json();
        console.log('🔍 First question data:', questionsData.questions[0]);
        setQuestions(questionsData.questions);
        setMockTestStarted(true);
        setShowJobForm(false);
        setTimeRemaining((credits >= 999000) ? 90 * 60 : 45 * 60);
        // Reset sequential-walkthrough state for a fresh test — they
        // start on Q1 with Q2-15 locked until they Save & Next through.
        setCurrentQuestionIndex(0);
        setFurthestReached(0);

        // Kick off background top-up — fetches questions 2..15 with
        // the already-generated ones excluded so the server doesn't
        // duplicate. Failures are non-fatal: candidate still has Q1.
        const firstQuestion = questionsData.questions[0]?.question;
        (async () => {
          try {
            const topup = await fetch(`${API_BASE_URL}/api/mock-interview/generate-questions`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                jobRole: formData.jobRole,
                experienceLevel: formData.experienceLevel,
                company: formData.company,
                jobDescription: formData.jobDescription,
                resumeText: formData.resumeText,
                count: 14,
                existingQuestions: firstQuestion ? [firstQuestion] : [],
              })
            });
            if (!topup.ok) throw new Error(`HTTP ${topup.status}`);
            const topupData = await topup.json();
            const extra = Array.isArray(topupData?.questions) ? topupData.questions : [];
            if (extra.length) {
              setQuestions(prev => [...prev, ...extra].slice(0, 15));
            }
          } catch (e) {
            console.warn('[MockInterview] background top-up failed:', e?.message);
          }
        })();
      } catch (error) {
        alert(error.message || 'Failed to start mock test. Please try again.');
      } finally {
        setIsGeneratingQuestions(false);
      }
    } else {
      alert('Please complete all required fields before starting the mock test.');
    }
  };

  const startRecording = async (questionIndex) => {
    try {
      setRecordingError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Save the audio for this question
        setAnswers(prev => ({
          ...prev,
          [questionIndex]: {
            audio: audioBlob,
            recordedAt: new Date().toISOString()
          }
        }));
        
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setRecordingError('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const deleteRecording = (questionIndex) => {
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionIndex];
      return newAnswers;
    });
  };

  const handleSubmitTest = async () => {
    const unansweredCount = questions.length - Object.keys(answers).length;
    
    if (unansweredCount > 0) {
      const confirm = window.confirm(
        `You have ${unansweredCount} unanswered question(s). Do you want to submit anyway?`
      );
      if (!confirm) return;
    }

    try {
      setIsEvaluating(true);
      
      // Prepare form data with audio files
      const formDataToSend = new FormData();
      formDataToSend.append('jobRole', formData.jobRole);
      formDataToSend.append('company', formData.company || '');
      formDataToSend.append('experienceLevel', formData.experienceLevel);
      formDataToSend.append('jobDescription', formData.jobDescription);
      formDataToSend.append('resumeText', formData.resumeText);
      formDataToSend.append('questions', JSON.stringify(questions));
      formDataToSend.append('duration', 45 * 60 - timeRemaining);
      
      // Add audio files
      Object.entries(answers).forEach(([questionIndex, answer]) => {
        formDataToSend.append(`audio_${questionIndex}`, answer.audio, `answer_${questionIndex}.webm`);
      });

      const response = await fetch(`${API_BASE_URL}/api/mock-interview/evaluate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to evaluate test');
      }

      const evaluationData = await response.json();
      setEvaluation(evaluationData);

      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      // Hand the report off to its own page. sessionStorage backs the
      // router state so a refresh on the results route survives.
      const resultsPayload = { evaluation: evaluationData, questions, formData };
      try { sessionStorage.setItem('mockInterviewResults', JSON.stringify(resultsPayload)); } catch (e) { /* quota — ignore */ }
      navigate('/mock-interview-results', { state: resultsPayload });
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Failed to evaluate test. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleEndTest = async (isAutoEnd = false) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    if (isAutoEnd) {
      alert('Time is up! Your test will now be evaluated.');
      await handleSubmitTest();
    } else {
      const confirm = window.confirm('Are you sure you want to end the test?');
      if (confirm) {
        navigate('/dashboard');
      }
    }
  };

  const handleHeaderNavigation = async (path) => {
    if (mockTestStarted && !evaluation) {
      const confirmEnd = window.confirm(
        'Mock test is in progress. Do you want to end the test? Your progress will be lost.'
      );
      if (!confirmEnd) return;
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    if (path === '/logout-redirect') {
      try {
        await fetch(`${API_BASE_URL}/api/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ email: user?.email })
        });
      } catch (error) {
        console.error('Logout API error:', error);
      }
      
      logout();
      navigate('/');
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="neural-glass-homepage">
        <div className="neural-background"></div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#fff',
          fontSize: '1.2rem'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Session-mode handling lives in MockInterviewLanding.css now: when
  // the body has `mock-interview-active`, all sibling sections of
  // .mil-studio are hidden so the candidate sees only the test panel
  // beneath the global Header (matches the AI Interview Assistant UX,
  // which keeps the page chrome but devotes the rest of the viewport
  // to the live experience). The body class is added in the useEffect
  // above.
  return (
    <div className={embedded ? 'mock-interview-embedded-root' : `neural-glass-homepage ${themeClass}`}>
      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      {showJobForm && createPortal(
        <div
          className="job-form-popup-overlay"
          onClick={() => setShowJobForm(false)}
          role="presentation"
        >
          <div
            className="job-form-popup glass"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="popup-header">
              <h3>Enter Job Information</h3>
              <button
                className="close-popup"
                onClick={() => setShowJobForm(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleStartMockTest();
            }}>
              <div className="form-group">
                <input
                  type="text"
                  name="jobRole"
                  placeholder="Job Role/Position *"
                  value={formData.jobRole}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              {/* Experience + Company side-by-side to save vertical
                  space — both fields are short, no need for a full row each. */}
              <div className="form-row-2">
                <div className="form-group">
                  <select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Experience Level *</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="company"
                    placeholder="Company (optional)"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="form-group form-group-ip">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#e0a3ff', fontWeight: '500' }}>Job Description *</label>
                  {uploadedJobDescFile && (
                    <div style={{ 
                      fontSize: '0.85rem', 
                      color: '#9370db', 
                      padding: '6px',
                      background: 'rgba(147, 112, 219, 0.1)',
                      borderRadius: '4px'
                    }}>
                      📄 {uploadedJobDescFile.name}
                    </div>
                  )}
                  <label htmlFor="job-desc-upload" style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem',
                    opacity: isProcessingFile ? 0.6 : 1
                  }}>
                    {isProcessingFile ? 'Processing...' : '📄 Upload File'}
                  </label>
                  <input
                    id="job-desc-upload"
                    type="file"
                    accept=".txt,.doc,.docx,.pdf"
                    onChange={(e) => handleFileUpload(e, 'jobDesc')}
                    disabled={isProcessingFile}
                    style={{ display: 'none' }}
                  />
                </div>
                <textarea
                  name="jobDescription"
                  placeholder="Paste job description or upload a file"
                  value={formData.jobDescription}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  disabled={isProcessingFile}
                ></textarea>
              </div>
              
              <div className="form-group form-group-ip">
                <label style={{ color: '#e0a3ff', fontWeight: '500', marginBottom: '8px', display: 'block' }}>Your Resume/CV *</label>
                {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                  <>
                    {/* CSS Grid with equal-width columns + explicit fixed
                        height guarantees both cards are pixel-identical. */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px',
                      marginBottom: '12px',
                    }}>
                      <button
                        type="button"
                        onClick={() => {
                          setResumeChoice('profile');
                          setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          width: '100%', height: 78, padding: '12px 14px',
                          borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${resumeChoice === 'profile' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'profile' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxSizing: 'border-box', fontFamily: 'inherit', margin: 0,
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>✅ Use Profile Resume</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {profileData.fullName || 'Use the resume on your profile'}
                        </div>
                      </button>
                      {/* Click on the card opens the OS file picker AND sets
                          the upload-mode choice — one gesture, no extra button. */}
                      <label
                        htmlFor="resume-upload"
                        onClick={() => {
                          setResumeChoice('upload');
                          setFormData(prev => ({ ...prev, resumeText: '' }));
                          setUploadedResumeFile(null);
                        }}
                        style={{
                          width: '100%', height: 78, padding: '12px 14px',
                          borderRadius: '8px',
                          cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                          opacity: isProcessingFile ? 0.6 : 1,
                          border: `2px solid ${resumeChoice === 'upload' ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                          background: resumeChoice === 'upload' ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                          color: 'white', fontSize: '0.85rem', transition: 'all 0.2s', textAlign: 'left',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                          boxSizing: 'border-box', margin: 0,
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>
                          {isProcessingFile ? '⏳ Processing…' : '📄 Upload New Resume'}
                        </div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {uploadedResumeFile ? uploadedResumeFile.name : 'Choose a file (PDF, DOCX, DOC, TXT)'}
                        </div>
                      </label>
                      <input
                        id="resume-upload"
                        type="file"
                        accept=".txt,.doc,.docx,.pdf"
                        onChange={(e) => handleFileUpload(e, 'resume')}
                        disabled={isProcessingFile}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {resumeChoice === 'profile' && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Your profile resume will be used for this mock interview session.
                      </div>
                    )}
                    {resumeChoice === 'upload' && formData.resumeText && (
                      <div style={{
                        padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem',
                        background: 'rgba(147,112,219,0.1)', border: '1px solid rgba(147,112,219,0.3)', color: '#e0a3ff'
                      }}>
                        ✓ Resume uploaded — ready to start your mock interview.
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback: user has no profile resume — single big upload
                  // card, no paste textarea.
                  <>
                    <label htmlFor="resume-upload" style={{
                      display: 'block', padding: '14px 16px', borderRadius: '8px',
                      cursor: isProcessingFile ? 'not-allowed' : 'pointer',
                      opacity: isProcessingFile ? 0.6 : 1,
                      border: `2px solid ${uploadedResumeFile ? '#9370db' : 'rgba(255,255,255,0.2)'}`,
                      background: uploadedResumeFile ? 'rgba(147,112,219,0.2)' : 'rgba(255,255,255,0.05)',
                      color: 'white', fontSize: '0.9rem', transition: 'all 0.2s', textAlign: 'left',
                    }}>
                      <div style={{ fontWeight: '600' }}>
                        {isProcessingFile ? '⏳ Processing…' : uploadedResumeFile ? '✓ Resume uploaded' : '📄 Upload your resume'}
                      </div>
                      <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: '3px' }}>
                        {uploadedResumeFile ? uploadedResumeFile.name : 'Choose a file (PDF, DOCX, DOC, TXT)'}
                      </div>
                    </label>
                    <input id="resume-upload" type="file" accept=".txt,.doc,.docx,.pdf"
                      onChange={(e) => handleFileUpload(e, 'resume')} disabled={isProcessingFile} style={{ display: 'none' }} />
                  </>
                )}
              </div>
              
              {fileUploadError && (
                <div style={{
                  padding: '10px',
                  marginBottom: '15px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: fileUploadError.startsWith('✓') 
                    ? 'rgba(76, 175, 80, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                  color: fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336',
                  border: `1px solid ${fileUploadError.startsWith('✓') ? '#4caf50' : '#f44336'}`
                }}>
                  {fileUploadError}
                </div>
              )}
              
              <button
                type="submit"
                className="cta-button"
                disabled={isProcessingFile || isGeneratingQuestions}
              >
                {isGeneratingQuestions ? 'Generating Questions...' : 'Start Mock Test (1 Credit)'}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      <div
        className={`mock-interview-page-container ${embedded ? 'mock-interview-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/mock-interview.png')" }}
      >
        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to start a mock interview.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {!embedded && (
          <>
            <Header
              onNavigate={handleHeaderNavigation}
              interviewInProgress={mockTestStarted && !evaluation}
            />
            <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />
          </>
        )}

        <main className="mock-interview-main">
          {/* Theme switch — parks itself beside the header pill; see
              `.cls-theme-dock` in studioTheme.css. */}
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                AI Mock<span className="li-gradient-text"> Interview</span>
              </h1>
              <p className="li-hero-subtitle">
                Practice smarter with AI mock interviews designed to sharpen your interview skills.
              </p>
              <CreditsBadge feature="mockInterview" label="Mock Interview" className="hero-credits-badge" />
            </div>
          )}
          {!mockTestStarted ? (
            <div className="mock-test-intro">
              <div className="">
                <p className="intro-subtitle">
                  Practice with 15 AI-generated interview questions tailored to your role
                </p>
                <button
                  onClick={() => {
                    if (credits < 1) {
                      setShowInsufficientCreditsModal(true);
                      return;
                    }
                    const hasProfileResume = profileData && (
                      profileData.professionalSummary ||
                      profileData.skills ||
                      (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))
                    );
                    if (hasProfileResume && !resumeChoice) {
                      setResumeChoice('profile');
                      setFormData(prev => ({ ...prev, resumeText: buildResumeTextFromProfile(profileData) }));
                    } else if (!hasProfileResume && !resumeChoice) {
                      setResumeChoice('upload');
                    }
                    setShowJobForm(true);
                  }}
                  className="start-mock-test-btn"
                >
                  Start Mock Test
                </button>
              </div>

            {!embedded && (
            <>
            {/* Why Use TERVIEW Mock Test */}
            <section className="info-section glass">
              <h2 className="info-section-title">Why Use TERVIEW Mock Interview?</h2>
              <div className="info-grid">
                <div className="info-card glass">
                  <div className="info-icon">🎯</div>
                  <h3>AI-Tailored Questions</h3>
                  <p>Every question is generated specifically for your job role, experience level, and resume. No generic question banks — every session is unique to your career profile.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🎙️</div>
                  <h3>Speak & Get Scored</h3>
                  <p>Record your answers out loud just like a real interview. Our AI evaluates your spoken delivery, content coverage, and clarity — giving you a real readiness score.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">📊</div>
                  <h3>Detailed Feedback</h3>
                  <p>Get per-question scores with actionable advice on what you covered well and what to improve. Track your progress across sessions in your dashboard.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🧠</div>
                  <h3>Learn Model Answers</h3>
                  <p>Each question comes with a full model answer written by AI. Read it, internalize it, and record yourself saying it — the best way to absorb interview-ready language.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">⏱️</div>
                  <h3>Timed Practice</h3>
                  <p>Practice under real interview pressure with a countdown timer. Build the stamina and focus needed to answer 15 questions confidently in a single sitting.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🔒</div>
                  <h3>Safe Environment</h3>
                  <p>Make mistakes here, not in the real interview. Practice as many times as you need until your answers feel natural, confident, and polished.</p>
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section className="how-it-works-section glass">
              <h2 className="info-section-title">How It Works</h2>
              <div className="steps-grid">
                <div className="step-card glass">
                  <div className="step-number">1</div>
                  <h4>Enter Job Details</h4>
                  <p>Provide your target job role, experience level, company name, and paste or upload the job description. The AI uses all of this to craft relevant questions.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">2</div>
                  <h4>Upload Your Resume</h4>
                  <p>Upload your resume (PDF, DOCX, or TXT). The AI reads your background and tailors both the questions and model answers to your actual experience.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">3</div>
                  <h4>Read the Model Answer</h4>
                  <p>Each question displays a full AI-generated model answer. Read it carefully so you understand what a great response looks like before recording.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">4</div>
                  <h4>Record Your Answer</h4>
                  <p>Hit Record and speak the answer aloud. Our system uses Whisper AI to transcribe your speech accurately, even with technical jargon and proper nouns.</p>
                </div>
                <div className="step-card glass">
                  <div className="step-number">5</div>
                  <h4>Get Your Evaluation</h4>
                  <p>Submit the test and receive a detailed evaluation with an overall score, per-question breakdown, content quality scores, delivery ratings, and improvement advice.</p>
                </div>
              </div>
            </section>

            {/* Tips Section */}
            <section className="tips-section glass">
              <h2 className="info-section-title">Mock Interview Tips</h2>
              <div className="tips-columns">
                <div className="tip-column glass">
                  <h4>🎯 Before You Start</h4>
                  <ul>
                    <li>Read the job description carefully and note key skills required</li>
                    <li>Have an updated, detailed resume ready to upload</li>
                    <li>Find a quiet room with no background noise for clear recording</li>
                    <li>Test your microphone before starting the session</li>
                    <li>Set aside at least 45–60 uninterrupted minutes</li>
                    <li>Have a glass of water nearby — speaking 15 answers is a workout</li>
                  </ul>
                </div>
                <div className="tip-column glass">
                  <h4>🎙️ During the Test</h4>
                  <ul>
                    <li>Read the model answer fully before hitting Record</li>
                    <li>Speak clearly and at a natural conversational pace</li>
                    <li>Don't rush — quality delivery scores higher than speed</li>
                    <li>If you stumble, stop recording and re-record the answer</li>
                    <li>Use the question navigator to skip and return to hard questions</li>
                    <li>Watch the timer — aim to answer each question in 2–3 minutes</li>
                  </ul>
                </div>
                <div className="tip-column glass">
                  <h4>📈 After Your Results</h4>
                  <ul>
                    <li>Review every question where you scored below 70 carefully</li>
                    <li>Re-read the model answers for low-scoring questions</li>
                    <li>Take notes on the AI advice for each question</li>
                    <li>Retake the test for the same role to track improvement</li>
                    <li>Practice the hardest questions daily until they feel natural</li>
                    <li>Use your scores to identify weak topic areas to study further</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Benefits Banner */}
            <section className="info-section glass">
              <h2 className="info-section-title">What You Get With Every Mock Test</h2>
              <div className="info-grid">
                <div className="info-card glass">
                  <div className="info-icon">✅</div>
                  <h3>15 Personalized Questions</h3>
                  <p>A mix of behavioral, technical, situational, and resume-based questions — covering every angle an interviewer might take.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">📝</div>
                  <h3>Full Model Answers</h3>
                  <p>Every question includes a complete, first-person model answer written specifically for your role and experience level.</p>
                </div>
                <div className="info-card glass">
                  <div className="info-icon">🏆</div>
                  <h3>Overall Readiness Score</h3>
                  <p>Walk away knowing exactly where you stand — and what to work on before your real interview day.</p>
                </div>
              </div>
            </section>
            </>
            )}

          </div>
          ) : (
            <div className="mock-test-interface">
              <div className="test-header glass">
                <div className="test-info">
                  <h2>{formData.jobRole} - Mock Interview</h2>
                  <p>Question {currentQuestionIndex + 1} of {MOCK_TOTAL_QUESTIONS}</p>
                </div>
                <div className="test-controls">
                  <div className="timer" style={{
                    padding: '8px 16px',
                    background: timeRemaining < 300 ? '#ff6b6b' : '#9370db',
                    borderRadius: '8px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {formatTime(timeRemaining)}
                  </div>
                  <button onClick={() => handleEndTest(false)} className="end-button">
                    End Test
                  </button>
                </div>
              </div>

              <div className="question-panel glass">
                <div className="question-content">
                  <h3>Question {currentQuestionIndex + 1}</h3>
                  <p className="question-text">{questions[currentQuestionIndex]?.question}</p>

                  <div style={{
                    marginTop: '20px',
                    background: 'rgba(147, 112, 219, 0.12)',
                    border: '1px solid rgba(147, 112, 219, 0.4)',
                    borderLeft: '4px solid #9370db',
                    borderRadius: '10px',
                    padding: '18px 20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1.1rem' }}>📖</span>
                      <strong style={{ color: '#e0a3ff', fontSize: '1rem' }}>
                        Read & Record This Answer:
                      </strong>
                    </div>
                    <p style={{
                      color: '#f0e0ff',
                      lineHeight: '1.9',
                      fontSize: '1rem',
                      whiteSpace: 'pre-wrap',
                      margin: 0
                    }}>
                      {questions[currentQuestionIndex]?.expectedAnswer 
                        || questions[currentQuestionIndex]?.answer
                        || questions[currentQuestionIndex]?.sampleAnswer
                        || questions[currentQuestionIndex]?.ideal_answer
                        || 'Answer will appear here. Check your API response includes an answer field.'}
                    </p>
                    <p style={{
                      fontSize: '0.78rem',
                      color: 'rgba(255,255,255,0.45)',
                      marginTop: '14px',
                      marginBottom: 0,
                      fontStyle: 'italic'
                    }}>
                      🎤 Read the answer above out loud, then hit Record below.
                    </p>
                  </div>
                </div>

                <div className="recording-section">
                  {recordingError && (
                    <div className="recording-error">{recordingError}</div>
                  )}
                  
                  {answers[currentQuestionIndex] ? (
                    <div className="recorded-answer">
                      <div className="recorded-indicator">
                        <span className="check-icon">✓</span>
                        <span>Answer Recorded</span>
                      </div>
                      <button 
                        onClick={() => deleteRecording(currentQuestionIndex)}
                        className="delete-recording-btn"
                      >
                        Re-record Answer
                      </button>
                    </div>
                  ) : (
                    <div className="recording-controls">
                      {!isRecording ? (
                        <button 
                          onClick={() => startRecording(currentQuestionIndex)}
                          className="record-btn"
                        >
                          <span className="record-icon">🎤</span>
                          Record Answer
                        </button>
                      ) : (
                        <button 
                          onClick={stopRecording}
                          className="stop-recording-btn recording-active"
                        >
                          <span className="recording-pulse"></span>
                          Stop Recording
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="navigation-controls">
                <button 
                  onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestionIndex === 0}
                  className="nav-btn"
                >
                  ← Previous
                </button>
                
                <div className="question-indicators">
                  {Array.from({ length: MOCK_TOTAL_QUESTIONS }, (_, index) => {
                    const generated = index < questions.length;
                    const unlocked  = generated && index <= furthestReached;
                    const isLoading = !generated;
                    return (
                      <button
                        key={index}
                        onClick={() => unlocked && setCurrentQuestionIndex(index)}
                        disabled={!unlocked}
                        title={
                          isLoading ? `Question ${index + 1} is loading…`
                          : !unlocked ? `Question ${index + 1} unlocks after you complete the previous ones`
                          : `Question ${index + 1}`
                        }
                        className={`question-indicator ${
                          index === currentQuestionIndex ? 'active' : ''
                        } ${answers[index] ? 'answered' : ''} ${unlocked ? '' : 'locked'} ${isLoading ? 'loading-q' : ''}`}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>

                {currentQuestionIndex < MOCK_TOTAL_QUESTIONS - 1 ? (() => {
                  // Save & Next is the primary forward action — gradient
                  // styled like Submit so the candidate's eye lands on
                  // it. Disabled when the next question hasn't arrived
                  // from the background top-up yet (rare since they're
                  // usually on Q1 for 60-90s while we fetch 2-15).
                  const nextLoaded = currentQuestionIndex + 1 < questions.length;
                  return (
                    <button
                      onClick={() => {
                        if (!nextLoaded) return;
                        const next = currentQuestionIndex + 1;
                        setCurrentQuestionIndex(next);
                        setFurthestReached(prev => Math.max(prev, next));
                      }}
                      disabled={!nextLoaded}
                      title={!nextLoaded ? 'Loading the next question…' : ''}
                      className="submit-btn test-btn save-next-btn"
                    >
                      {nextLoaded ? 'Save & Next →' : 'Loading next…'}
                    </button>
                  );
                })() : (
                  <button
                    onClick={handleSubmitTest}
                    disabled={isEvaluating || questions.length < MOCK_TOTAL_QUESTIONS}
                    className="submit-btn test-btn"
                    title={questions.length < MOCK_TOTAL_QUESTIONS ? 'Waiting for the rest of the questions…' : ''}
                  >
                    {isEvaluating ? 'Evaluating...' : 'Submit Test'}
                  </button>
                )}
              </div>
            </div>
          )}
        </main>

        {!embedded && <Footer />}
      </div>

      {/* Branded exit-confirm modal — replaces window.confirm() with the
          same "End the session?" pattern used in the AI Interview Assistant. */}
      {showExitConfirm && createPortal(
        <div
          className="mip-exit-overlay"
          onClick={() => { setShowExitConfirm(false); exitTargetRef.current = null; }}
          role="presentation"
        >
          <div
            className="mip-exit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mip-exit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mip-exit-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 id="mip-exit-title" className="mip-exit-title">End the mock interview?</h3>
            <p className="mip-exit-body">
              Leaving this page will end your test. Your progress so far
              will be saved to your dashboard so you can review it later.
            </p>
            <div className="mip-exit-actions">
              <button
                type="button"
                className="mip-exit-btn mip-exit-cancel"
                onClick={() => { setShowExitConfirm(false); exitTargetRef.current = null; }}
              >
                Stay
              </button>
              <button
                type="button"
                className="mip-exit-btn mip-exit-confirm"
                onClick={() => {
                  setShowExitConfirm(false);
                  const target = exitTargetRef.current;
                  exitTargetRef.current = null;
                  bypassGuardRef.current = true;
                  // Tear down the test cleanly so cleanup effects run.
                  setMockTestStarted(false);
                  if (target && typeof target.click === 'function') {
                    target.click();
                  } else {
                    window.history.back();
                  }
                  setTimeout(() => { bypassGuardRef.current = false; }, 200);
                }}
              >
                Yes, end session
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MockInterviewPage;